import React from 'react';
import { Link } from 'react-router-dom';
import useDms from '../../hooks/useDms';
import { Loading, ErrorBox } from './shared';
import { nfmt, compactUsd } from '../../lib/formatters';

function BarList({ items, getLabel, getValue, formatter = nfmt }) {
  const sorted = [...items].sort((a, b) => (getValue(b) || 0) - (getValue(a) || 0)).slice(0, 10);
  const max = Math.max(1, ...sorted.map((i) => getValue(i) || 0));
  return (
    <div className="bars">
      {sorted.map((it, i) => (
        <div className="bar-row" key={i}>
          <span className="bar-label">{getLabel(it)}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${((getValue(it) || 0) / max) * 100}%` }} />
          </div>
          <span className="bar-value">{formatter(getValue(it))}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportsOverview() {
  const summary = useDms('summary');
  const datasets = useDms('datasets');

  if (summary.loading || datasets.loading) return <Loading label="Loading reports…" />;
  if (summary.error) return <ErrorBox error={summary.error} />;
  if (datasets.error) return <ErrorBox error={datasets.error} />;

  const h = summary.data?.headline || {};
  const ds = datasets.data?.datasets || [];

  const kpis = [
    { label: 'Tours', value: h.tours, sub: 'tour box-score posts', icon: '◈', money: false },
    { label: 'Engagements', value: h.engagements, sub: 'venue engagements', icon: '▦', money: false },
    { label: 'Artists', value: h.artists, sub: 'with touring data', icon: '♫', money: false },
    { label: 'Tour gross', value: h.tours_revenue_usd, sub: 'all tours, USD', icon: '$', money: true },
    { label: 'Tickets sold', value: h.tours_tickets_sold, sub: 'all tours', icon: '♪', money: false },
    { label: '2026 box scores', value: h.boxscore_rows_2026, sub: 'reported shows', icon: '☀', money: false },
  ];

  const groups = {};
  for (const d of ds) groups[d.group] = (groups[d.group] || 0) + 1;

  return (
    <div className="lyve-intel">
      <div className="hero" style={{ paddingBottom: 0 }}>
        <div className="eyebrow">
          <i />
          REPORTS
        </div>
        <h1 className="h-title wide">REPORTS</h1>
        <p className="h-sub">
          Touring data, box scores, shows and resale/ticket-sales data compiled from touringdata.org, the 2026 Master
          Document and SeatData.
        </p>
      </div>

      <div className="page" style={{ paddingTop: 18 }}>
        <div className="rp-grid rp-cols-4" style={{ marginBottom: 18 }}>
          {kpis.map((k) => (
            <div className="rp-card rp-stat" key={k.label}>
              <div className="glow" />
              <div className="label">
                {k.icon} {k.label}
              </div>
              <div className="value" style={{ fontSize: k.money ? 26 : undefined }}>
                {k.money ? compactUsd(k.value) : nfmt(k.value)}
              </div>
              <div className="sub">{k.sub}</div>
            </div>
          ))}
        </div>

        <div className="rp-grid rp-cols-2">
          <div className="rp-card">
            <div className="section-title" style={{ marginTop: 0 }}>
              <h2>Top artists by revenue</h2>
              <Link to="/reports/artists" className="rp-hint">
                All artists →
              </Link>
            </div>
            <BarList
              items={summary.data?.top_artists_by_revenue || []}
              getLabel={(a) => a.artist}
              getValue={(a) => a.revenue_usd}
              formatter={compactUsd}
            />
          </div>

          <div className="rp-card">
            <div className="section-title" style={{ marginTop: 0 }}>
              <h2>Engagements by country</h2>
              <Link to="/reports/tours" className="rp-hint">
                Tours →
              </Link>
            </div>
            <BarList
              items={summary.data?.engagements_by_country || []}
              getLabel={(c) => c.country}
              getValue={(c) => c.count}
            />
          </div>

          <div className="rp-card">
            <div className="section-title" style={{ marginTop: 0 }}>
              <h2>2023 year-end top acts</h2>
              <Link to="/reports/artists" className="rp-hint">
                Profiles →
              </Link>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Artist</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(summary.data?.year_end_2023_top || []).map((y) => (
                  <tr key={y.rank}>
                    <td>{y.rank}</td>
                    <td>{y.artist}</td>
                    <td>{compactUsd(y.revenue_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rp-card">
            <div className="section-title" style={{ marginTop: 0 }}>
              <h2>Datasets</h2>
              <Link to="/reports/boxscores" className="rp-hint">
                Box scores →
              </Link>
            </div>
            <div className="rchips">
              {Object.entries(groups).map(([g, n]) => (
                <span className="rchip" key={g}>
                  {g} · {n}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <Link className="rp-back" to="/reports/shows">
                View show records →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
